import type { StockResponse, StockWithLocationResponse } from 'tm3-core/api.dtos';

import createContext from 'bz-utilities/hooks/context-v2';
import CacheStore from 'bz-utilities/modules/stores/cached-map-store';
import GUID from 'bz-utilities/modules/variables/guid';
import { computed } from 'mobx';
import type { ITransformer } from 'mobx-utils';
import { createTransformer } from 'mobx-utils';
import type StockCategory from 'tm3-core/interfaces/stock/enums/category';
import { cache } from '../index';

type IStockKeys =
  | 'id'
  | 'reference'
  | 'category'
  | 'appointmentLength'
  | 'isMultiple'
  | 'isPrivateCharge'
  | 'serviceTypeId'
  | 'isVideo';

export const [InventoryStoreCtx, useInventoryStore] =
  createContext<InventoryStore>('InventoryStore');

export interface IChargeControlStock extends Pick<StockResponse, IStockKeys> {
  isPrePaid: boolean;
  name: string;
}

class InventoryStore extends CacheStore<StockWithLocationResponse> {
  constructor() {
    super('inventory');
  }
  @computed
  get Current() {
    return this.All.filter((x) => x.inUse);
  }

  @computed
  get Online() {
    return this.All.filter((x) => x.inUse && x.online && !x.locationId);
  }

  @computed
  get AllOnline() {
    return this.All.filter((x) => x.online && !x.locationId);
  }

  public search(
    searchTerm: string = '',
    privateOnlyCharges = false,
    allowedCatergories: StockCategory[] = []
  ): StockWithLocationResponse[] {
    if (!searchTerm.trim()) return []; // Return early if searchTerm is empty or whitespace

    const lowerSearchTerm = searchTerm.toLowerCase();

    return this.Current.filter((stock) => {
      const matchesCategory =
        !allowedCatergories.length || allowedCatergories.includes(stock.category as StockCategory);
      const matchesPrivateCharge = !privateOnlyCharges || !!stock.isPrivateCharge; // Check private charges only if flag is true
      const matchesDescription = (stock.description ?? '').toLowerCase().includes(lowerSearchTerm);

      return matchesCategory && matchesPrivateCharge && matchesDescription; // Combine conditions
    });
  }

  ByLocationWithPrice = createTransformer((locationId: number) => {
    return this.Current.filter((x) => !x.locationId || x.locationId === locationId).map((x) =>
      this.dropDownFormat(x, true, locationId)
    );
  });

  ByLocation = createTransformer((locationId: number) => {
    return this.Current.filter((x) => !x.locationId || x.locationId === locationId).map((x) =>
      this.dropDownFormat(x, false, locationId)
    );
  });

  ForAppointmentSelection: ITransformer<AppointmentStockSelectionParams, IChargeControlStock[]> =
    createTransformer((args: AppointmentStockSelectionParams) => {
      const { FILTERMODE = 'OFF' } = cache.config;

      const practitioner = cache.practitioners.getById(args.practitionerId);

      return this.All.filter((stock: StockWithLocationResponse) => {
        const {
          locationId,
          serviceTypeId,
          overrides,
          types,
          excludeMulti,
          skipFilterMode,
          groupId,
          sessionSkillRestrictions
        } = args;

        // if it's an override it should be included regardless of filtering.
        if (overrides.includes(stock.id)) {
          return true;
        }

        // exclude deactivated stock
        if (!stock.inUse) {
          return false;
        }

        // check stock is of the correct service type id
        if (serviceTypeId > 0 && stock.serviceTypeId !== serviceTypeId) {
          return false;
        }

        // check category filter condition
        if (types.length > 0 && !types.includes(stock.category as 'B' | 'C' | 'S' | 'P')) {
          return false;
        }

        // exclude any multi stock if required
        if (excludeMulti && stock.isMultiple) {
          return false;
        }

        // if using strict or flex filter modes need to check group vs private stock
        if (FILTERMODE !== 'OFF' && !skipFilterMode) {
          if (groupId > 0 ? !stock.groups.includes(groupId) : !stock.isPrivateCharge) {
            return false;
          }
        }

        // if it has a skill restriction on the session
        if (
          !skipFilterMode &&
          sessionSkillRestrictions?.length > 0 &&
          (!stock.specialismId || !sessionSkillRestrictions.includes(stock.specialismId))
        )
          return false;

        // if it has a skill restriction on the practitioners
        if (
          !skipFilterMode &&
          stock.specialismId &&
          !GUID.isEmpty(stock.specialismId) &&
          (!practitioner || !practitioner.skills.includes(stock.specialismId))
        )
          return false;

        if (!!cache.isClick) {
          // if click no need to check online status
          return !stock.locationId || stock.locationId === locationId;
        }

        // if it's online it'll have no location id, otherwise, compare on location id
        return (!stock.locationId && stock.online) || stock.locationId === locationId;
      }).map((x) => this.dropDownFormat(x, args.showPrice, args.locationId));
    });

  dropDownPrepaidFormat(stock: StockWithLocationResponse): IChargeControlStock {
    let name = stock.description;
    if (stock.appointmentLength != null) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null) {
      name = stock.reference + ' - ' + name;
    }
    name += ' [PrePaid]';
    return {
      id: stock.id,
      name,
      reference: stock.reference || '',
      category: stock.category,
      appointmentLength: stock.appointmentLength,
      isMultiple: stock.isMultiple,
      isPrivateCharge: stock.isPrivateCharge,
      isPrePaid: true,
      serviceTypeId: stock.serviceTypeId,
      isVideo: stock.isVideo
    };
  }

  dropDownFormat(
    stock: StockWithLocationResponse,
    showPrice: boolean,
    locationId: number
  ): IChargeControlStock {
    const {
      config: { CURRENCYSYMBOL = '£' }
    } = cache;
    let name;
    if (showPrice) {
      let price = stock.totalPrice;
      const stockOverride = stock.overrides.find((x) => x.locationId === locationId);
      if (stockOverride) {
        price = stockOverride.totalPrice;
      }
      name = stock.description + ' - ' + CURRENCYSYMBOL + ' ' + price.toFixed(2);
    } else {
      name = stock.description;
    }
    if (stock.appointmentLength != null) {
      name = name + ' - ' + stock.appointmentLength + ' minute(s)';
    }
    if (stock.reference != null) {
      name = stock.reference + ' - ' + name;
    }
    return {
      id: stock.id,
      name,
      reference: stock.reference || '',
      category: stock.category,
      appointmentLength: stock.appointmentLength,
      isMultiple: stock.isMultiple,
      isPrivateCharge: stock.isPrivateCharge,
      isPrePaid: false,
      serviceTypeId: stock.serviceTypeId,
      isVideo: stock.isVideo
    };
  }
}

export class AppointmentStockSelectionParams {
  locationId: number = -1;
  groupId: number = -1;
  practitionerId: number = -1;
  serviceTypeId: number = 1;
  types: ('B' | 'C' | 'S' | 'P')[] = ['C', 'S'];
  excludeMulti: boolean = false;
  skipFilterMode: boolean = false;
  overrides: number[] = [];
  showPrice: boolean = false;
  sessionSkillRestrictions: string[] = [];

  public constructor(init?: Partial<AppointmentStockSelectionParams>) {
    // c# object initialiser  style nearly....
    (Object as any).assign(this, init);
  }
}

export default InventoryStore;
